/**
 * LayoutDropdown Component
 *
 * Popover-based icon-grid layout selector (TradingView-style).
 * Rows grouped by preset family, each row shows a family label and a
 * horizontal strip of clickable LayoutPresetPreview icons. Banner toggle
 * sits below the grid with per-widget visibility toggles and position selector.
 * Popover handles dismiss/focus-return natively.
 *
 * Lives in the TradingHub header's actionMenuSlot during live-trading phase.
 *
 * @see constants/layout.ts for preset definitions and family grouping
 * @see constants/bannerWidget.ts for BANNER_WIDGETS registry
 * @see types/shared/Layout.ts for LayoutPresetId, LayoutPresetFamily, BannerPosition types
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  LayoutDashboard, Check, Eye, EyeOff, PanelTopDashed, PanelBottomDashed,
  ChevronUp, ChevronDown, AlignLeft, AlignCenter, AlignRight,
} from 'lucide-react';

import {
  Button, ButtonGroup,
  Popover, PopoverTrigger, PopoverContent,
  Divider, Tooltip, Switch,
} from '@enfomentis/ui';
import { BANNER_WIDGETS } from '@/constants/bannerWidget';
import {
  LAYOUT_PRESETS,
  LAYOUT_PRESETS_BY_FAMILY,
  LAYOUT_PRESET_FAMILY_DISPLAY_MAP,
  PHASE_AVAILABLE_PRESETS,
  MOBILE_DISTINCT_PRESETS,
  getMobileEquivalentPresetId,
} from '@/constants/layout';
import { useIsDesktop } from '@/hooks/useIsDesktop';

import { LayoutPresetPreview } from './LayoutPresetPreview';

import type { LayoutPresetId, LayoutPresetFamily, BannerAlignment, BannerPosition, BannerWidgetId } from '@/types/shared';
import type { ResolvedBannerConfig } from '@/hooks/useLayoutConfig';

// ===== CONSTANTS =====

/** Ordered families for display; only families with phase-available presets appear */
const FAMILY_ORDER: readonly LayoutPresetFamily[] = ['2-pane', '3-pane', '4-pane', '5-pane', '6-pane'];

/** Position selector options for ButtonGroup */
const POSITION_OPTIONS = [
  { label: 'Top', value: 'top', icon: PanelTopDashed },
  { label: 'Bottom', value: 'bottom', icon: PanelBottomDashed },
] as const;

/** Alignment selector options for ButtonGroup (single-widget mode) */
const ALIGNMENT_OPTIONS = [
  { label: 'Left', value: 'start', icon: AlignLeft },
  { label: 'Center', value: 'center', icon: AlignCenter },
  { label: 'Right', value: 'end', icon: AlignRight },
] as const;

// ===== TYPES =====

export interface LayoutDropdownProps {
  activePresetId: LayoutPresetId;
  resolvedBannerConfig: ResolvedBannerConfig;
  onPresetChange: (presetId: LayoutPresetId) => void;
  onBannerToggle: () => void;
  onBannerPositionChange: (position: BannerPosition) => void;
  onWidgetToggle: (widgetId: BannerWidgetId) => void;
  onWidgetOrderChange: (order: BannerWidgetId[]) => void;
  onWidgetAlignmentChange: (alignment: BannerAlignment) => void;
}

// ===== COMPONENT =====

export const LayoutDropdown: React.FC<LayoutDropdownProps> = ({
  activePresetId,
  resolvedBannerConfig,
  onPresetChange,
  onBannerToggle,
  onBannerPositionChange,
  onWidgetToggle,
  onWidgetOrderChange,
  onWidgetAlignmentChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const isDesktop = useIsDesktop();

  const availableFamilies = useMemo(() => {
    const allowedPresets = isDesktop
      ? PHASE_AVAILABLE_PRESETS
      : PHASE_AVAILABLE_PRESETS.filter((id) =>
          (MOBILE_DISTINCT_PRESETS as readonly string[]).includes(id));

    return FAMILY_ORDER
      .map((family) => ({
        family,
        presets: LAYOUT_PRESETS_BY_FAMILY[family]
          .filter((id) => (allowedPresets as readonly string[]).includes(id)),
      }))
      .filter(({ presets }) => presets.length > 0);
  }, [isDesktop]);

  const effectiveActivePresetId = isDesktop
    ? activePresetId
    : getMobileEquivalentPresetId(activePresetId);

  const handlePresetSelect = useCallback((presetId: LayoutPresetId) => {
    onPresetChange(presetId);
    setIsOpen(false);
  }, [onPresetChange]);

  const handlePositionChange = useCallback((value: string | number | (string | number)[] | null) => {
    // Guard against null (ButtonGroup emits null on re-click of selected option)
    if (value === 'top' || value === 'bottom') {
      onBannerPositionChange(value);
    }
  }, [onBannerPositionChange]);

  const { visible: bannerVisible, position, widgets, widgetOrder, widgetAlignment } = resolvedBannerConfig;

  // All widgets in user-defined order with registry fallback (research finding 6)
  const orderedWidgets = useMemo(() => {
    const byId = new Map(BANNER_WIDGETS.map((w) => [w.id, w]));
    const ordered: (typeof BANNER_WIDGETS)[number][] = [];
    for (const id of widgetOrder) {
      const widget = byId.get(id);
      if (widget) {
        ordered.push(widget);
        byId.delete(id);
      }
    }
    // Fallback: any remaining widgets in registry order
    for (const w of BANNER_WIDGETS) {
      if (byId.has(w.id)) {
        ordered.push(w);
      }
    }
    return ordered;
  }, [widgetOrder]);

  // Count enabled widgets for conditional alignment selector
  const enabledCount = useMemo(
    () => BANNER_WIDGETS.filter((w) => widgets[w.id] ?? w.defaultEnabled).length,
    [widgets],
  );

  // Reorder handler: derives effective order from orderedWidgets (not raw widgetOrder)
  // so fallback-positioned widgets can also be reordered
  const handleMoveWidget = useCallback((widgetId: BannerWidgetId, direction: 'up' | 'down') => {
    const effectiveOrder = orderedWidgets.map((w) => w.id);
    const idx = effectiveOrder.indexOf(widgetId);
    if (idx === -1) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= effectiveOrder.length) return;
    const a = effectiveOrder[idx];
    const b = effectiveOrder[swapIdx];
    if (a === undefined || b === undefined) return;
    effectiveOrder[idx] = b;
    effectiveOrder[swapIdx] = a;
    onWidgetOrderChange(effectiveOrder);
  }, [orderedWidgets, onWidgetOrderChange]);

  const handleAlignmentChange = useCallback((value: string | number | (string | number)[] | null) => {
    // Guard against null (ButtonGroup emits null on re-click of selected option)
    if (value === 'start' || value === 'center' || value === 'end') {
      onWidgetAlignmentChange(value);
    }
  }, [onWidgetAlignmentChange]);

  return (
    <Popover placement="bottom-end" isOpen={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger>
        <Button variant="light" size="sm" isIconOnly aria-label="Layout settings">
          <LayoutDashboard size={18} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-2">
        <div className="flex flex-col gap-1.5">
          {/* ── Layouts ── */}
          <span className="text-[10px] font-semibold uppercase tracking-wider text-default-400">Layouts</span>
          {availableFamilies.map(({ family, presets }) => (
            <div key={family} className="flex items-center gap-2">
              <span className="w-12 shrink-0 text-xs text-default-500">
                {LAYOUT_PRESET_FAMILY_DISPLAY_MAP[family]}
              </span>
              <div className="flex items-center gap-1">
                {presets.map((presetId) => {
                  const isActive = presetId === effectiveActivePresetId;
                  return (
                    <Tooltip
                      key={presetId}
                      content={LAYOUT_PRESETS[presetId].label}
                      placement="bottom"
                      delay={400}
                      closeDelay={0}
                    >
                      <Button
                        variant="light"
                        isIconOnly
                        size="sm"
                        aria-label={LAYOUT_PRESETS[presetId].label}
                        aria-pressed={isActive}
                        className={`relative overflow-visible ${isActive ? 'bg-primary/20 ring-1 ring-primary' : ''}`}
                        onPress={() => handlePresetSelect(presetId)}
                      >
                        <LayoutPresetPreview presetId={presetId} />
                        {isActive && (
                          <Check
                            size={10}
                            className="absolute -right-0.5 -top-0.5 rounded-full bg-primary text-primary-foreground p-px"
                          />
                        )}
                      </Button>
                    </Tooltip>
                  );
                })}
              </div>
            </div>
          ))}
          <Divider className="my-0.5" />
          {/* ── Banner Visibility & Layout ── */}
          <span className="text-[10px] font-semibold uppercase tracking-wider text-default-400">Banner Display</span>
          <div className="flex items-center gap-2">
            <Button
              variant="light"
              size="sm"
              className="w-full justify-start"
              aria-pressed={bannerVisible}
              startContent={
                bannerVisible
                  ? <Eye size={16} className="text-primary" />
                  : <EyeOff size={16} className="text-default-400" />
              }
              onPress={onBannerToggle}
            >
              Banner
            </Button>
            <ButtonGroup
              options={[...POSITION_OPTIONS]}
              value={position}
              onChange={handlePositionChange}
              size="sm"
              variant="flat"
              aria-label="Banner position"
              className={`[&>button]:min-w-24 ${bannerVisible ? '' : 'opacity-60'}`}
            />
          </div>
          <Divider className="my-0.5" />
          {/* ── Banner Widgets ── */}
          <span className="text-[10px] font-semibold uppercase tracking-wider text-default-400">Banner Widgets</span>
          <div className={`flex flex-col gap-1.5 ml-3 ${bannerVisible ? '' : 'opacity-60'}`}>
            {orderedWidgets.map((widget, index) => {
              const isEnabled = widgets[widget.id] ?? widget.defaultEnabled;
              return (
                <div key={widget.id} className="flex items-center gap-1">
                  <widget.Icon size={14} className="text-default-500 shrink-0" />
                  <span className="flex-1 text-xs text-default-700">{widget.label}</span>
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    className="min-h-11 min-w-11"
                    isDisabled={index === 0}
                    onPress={() => handleMoveWidget(widget.id, 'up')}
                    aria-label={`Move ${widget.label} up`}
                  >
                    <ChevronUp size={14} />
                  </Button>
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    className="min-h-11 min-w-11"
                    isDisabled={index === orderedWidgets.length - 1}
                    onPress={() => handleMoveWidget(widget.id, 'down')}
                    aria-label={`Move ${widget.label} down`}
                  >
                    <ChevronDown size={14} />
                  </Button>
                  <Switch
                    size="sm"
                    isSelected={isEnabled}
                    onValueChange={() => onWidgetToggle(widget.id)}
                    aria-label={`Toggle ${widget.label} widget`}
                  />
                </div>
              );
            })}
          </div>
          {/* ── Widget Alignment (single-widget mode) ── */}
          {enabledCount === 1 && (
            <>
              <span className={`text-[10px] font-semibold uppercase tracking-wider text-default-400 ${bannerVisible ? '' : 'opacity-60'}`}>Widget Alignment</span>
              <ButtonGroup
                options={[...ALIGNMENT_OPTIONS]}
                value={widgetAlignment}
                onChange={handleAlignmentChange}
                size="sm"
                variant="flat"
                aria-label="Widget alignment"
                className={`w-full [&>button]:flex-1 ${bannerVisible ? '' : 'opacity-60'}`}
              />
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
